import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { responsiveSpacing } from '../../themes/responsive';
import ExperimentsTable from '../../components/ExperimentsTable/ExperimentsTable';
import { useAuth } from '../../context/AuthContext';
import useExperimentTableData from '../../hooks/useExperimentTableData';

const createFilterSignature = (filters) => {
  if (!filters) {
    return '::';
  }

  const keys = Object.keys(filters);
  if (keys.length === 0) {
    return '::';
  }

  const sortedKeys = keys.sort();
  const parts = sortedKeys.map((key) => {
    const rawValue = filters[key];
    if (Array.isArray(rawValue)) {
      const normalized = [...rawValue]
        .map((value) => (value ?? '').toString())
        .sort();
      return `${key}:[${normalized.join(',')}]`;
    }
    const normalizedValue = rawValue ?? '';
    return `${key}:${normalizedValue.toString()}`;
  });

  return parts.join('|');
};

const createSorterSignature = (sorter) => {
  if (!sorter) {
    return '::';
  }

  const column = sorter.columnKey ?? '';
  const order = sorter.order ?? '';
  return `${column}:${order}`;
};

// Overview Tab Component (main content)
export const OverviewTab = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { currentUser } = useAuth();
  const jsLocale = { es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES';

  // Merge any filters passed via navigation state (e.g. from SlotDetailDrawer)
  const passedFilters = location.state?.filters;

  // Filter state for server-side filtering - start as null so the hook waits for table defaults
  const [tableQueryState, setTableQueryState] = React.useState(() => ({
    filters: {
      dynamicDateRange: [
        new Date(new Date().setMonth(new Date().getMonth() - 3)).toLocaleDateString(jsLocale),
        new Date().toLocaleDateString(jsLocale)
      ],
      status: ['running'],
      ...passedFilters
    },
    filterData: null,
    sorter: {
      columnKey: 'dynamicDeltaGrossProfit',
      order: 'descend'
    }
  }));
  const filtersSignatureRef = React.useRef(null);
  const sorterSignatureRef = React.useRef(null);
  
  // UI states
  const [selectedBaseline, setSelectedBaseline] = React.useState('historical'); // Default baseline comparison
  const [viewMode, setViewMode] = React.useState('unit'); // View mode: 'unit', 'group', or 'route'
  
  // Use the new table data hook for server-side filtering
  const {
    data: experimentsTableData,
    loading: loadingTable,
    error: tableError,
    pagination: tablePagination,
    setPage: setTablePage,
    totals,
    allExperimentIds,
    totalExperimentsCount
  } = useExperimentTableData(
    currentUser?.customer_id,
    tableQueryState.filters,
    {
      pageSize: 11,
      initialPage: 1,
      filterData: tableQueryState.filterData,
      sorter: tableQueryState.sorter,
      selectedBaseline: selectedBaseline,
      groupBy: viewMode === 'unit' ? null : viewMode
    }
  );
  
  // Toast notification state
  const [toast, setToast] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Toast helper functions
  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };


  // Handle table filter changes (called by ExperimentsTable component)
  const handleFiltersChange = React.useCallback((filters, filterData, sorter) => {
    const nextFilters = filters ?? {};
    const nextFiltersSignature = createFilterSignature(nextFilters);
    const filtersChanged = filtersSignatureRef.current !== nextFiltersSignature;

    const resolvedSorter = sorter !== undefined ? sorter : tableQueryState.sorter;
    const nextSorterSignature = createSorterSignature(resolvedSorter);
    const sorterChanged = sorterSignatureRef.current !== nextSorterSignature;

    const resolvedFilterData = filterData !== undefined ? filterData : tableQueryState.filterData;

    if (filtersChanged || sorterChanged || filterData) {
      if (filtersChanged) {
        filtersSignatureRef.current = nextFiltersSignature;
      }
      if (sorterChanged) {
        sorterSignatureRef.current = nextSorterSignature;
      }
      setTableQueryState({
        filters: nextFilters,
        filterData: resolvedFilterData,
        sorter: resolvedSorter ?? null
      });
    }
  }, [tableQueryState.filterData, tableQueryState.sorter]);
  

  // Show error toast if table data loading fails
  React.useEffect(() => {
    if (tableError) {
      showToast(t('failedToLoadExperimentsData'), 'error');
    }
  }, [tableError, showToast, t]);


  const handleTablePageChange = (page) => {
    setTablePage(page);
  };


  // Update global sidebar (empty for now)
  React.useEffect(() => {
    window.currentPageSidebar = null;
    window.dispatchEvent(new CustomEvent('sidebarUpdate'));
  }, []);

  // Clear sidebar when component unmounts
  React.useEffect(() => {
    return () => {
      window.currentPageSidebar = null;
      window.dispatchEvent(new CustomEvent('sidebarUpdate'));
    };
  }, []);

  return (
    <Box sx={{ 
      px: 0,
      py: responsiveSpacing.md,
      width: '100%',
      maxWidth: '100vw',
      boxSizing: 'border-box'
    }}>


      {/* Experiments Table */}
      <Box sx={{ px: responsiveSpacing.xs }}>
        <ExperimentsTable
          data={experimentsTableData}
          totalCount={tablePagination.totalCount}
          totalExperimentsCount={totalExperimentsCount}
          currentPage={tablePagination.currentPage}
          totalPages={tablePagination.totalPages}
          pageSize={tablePagination.pageSize}
          loading={loadingTable}
          selectedBaseline={selectedBaseline}
          onBaselineChange={setSelectedBaseline}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onPageChange={handleTablePageChange}
          onFiltersChange={handleFiltersChange}
          showToast={showToast}
          totals={totals}
          allExperimentIds={allExperimentIds}
          showKPIComparisonHint={false}
          initialFilters={passedFilters}
        />
      </Box>

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideToast} 
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Main ExperimentsOverview component
const ExperimentsOverview = () => {
  return <OverviewTab />;
};

export default ExperimentsOverview;
